import { useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  EditableToken,
  useCollectionBinding,
  useContentToken,
  usePage,
  useProperty,
} from '@vibesites/runtime/react';
import { cn } from '@vibesites/ui-primitives';
import pagesData from '../pages.json';

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  noindex?: boolean;
  template?: string;
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

/**
 * List pages for the HTML sitemap, following pages.json rules:
 * - `visible: false` → hidden
 * - `noindex: true` → excluded (same as sitemap.xml)
 * - `template` → definition only, not a real page
 * - virtual-tour / interactive-map gated like Header + SSG
 */
function buildSitemapPages(
  hasVirtualTours: boolean,
  hasInteractiveLeasing: boolean,
): PageEntry[] {
  return Object.entries(pagesData as Record<string, PageEntry>)
    .filter(([alias, page]) => {
      if (page.visible === false) return false;
      if (page.noindex) return false;
      if (page.template) return false;
      if (alias === 'sitemap' || page.path === '/sitemap') return false;
      if (page.path.includes('{{')) return false;
      if (alias === 'virtual-tour' && !hasVirtualTours) return false;
      if (alias === 'interactive-map' && !hasInteractiveLeasing) return false;
      return true;
    })
    .map(([, page]) => page)
    .sort((a, b) => a.position - b.position);
}

function pathsMatch(a: string | undefined, b: string | undefined): boolean {
  if (!a || !b) return false;
  const norm = (p: string) => {
    if (p === '/') return '/';
    return p.endsWith('/') ? p.slice(0, -1) : p;
  };
  return norm(a) === norm(b);
}

export function SitemapContent() {
  const heading = useContentToken('sitemapcontent.heading');
  const currentPath = usePage().path;
  const virtualTours = useCollectionBinding<VirtualTour>('sitemapcontent.virtual_tours', {
    collection: 'virtual_tours',
  });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  // Mirror SSG / Header: require both ApartmentSync IDs for Interactive Map.
  const property = useProperty() as Record<string, unknown>;
  const apartmentSyncJsId = property?.custom_apartmentsync_javascript_id as
    | string
    | null
    | undefined;
  const apartmentSyncConfigId = property?.custom_apartmentsync_config_id as
    | string
    | null
    | undefined;
  const hasInteractiveLeasing =
    !!(apartmentSyncJsId && String(apartmentSyncJsId).trim()) &&
    !!(apartmentSyncConfigId && String(apartmentSyncConfigId).trim());

  const pages = useMemo(
    () => buildSitemapPages(hasVirtualTours, hasInteractiveLeasing),
    [hasVirtualTours, hasInteractiveLeasing],
  );

  return (
    <section className="bg-background py-2xl md:py-3xl">
      <div className="mx-auto max-w-lg px-4 sm:px-6 lg:px-8">
        <EditableToken
          path="sitemapcontent.heading"
          as="h1"
          className="sr-only"
        >
          {heading || 'Sitemap'}
        </EditableToken>
        <nav aria-label="Sitemap" className="flex flex-col items-center">
          {pages.map((page) => {
            const isActive = pathsMatch(currentPath, page.path);
            return (
              <div
                key={page.path}
                className="flex w-full flex-col items-center"
              >
                <a
                  href={page.path}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(
                    'py-xl text-center font-heading text-base font-normal tracking-[0.08em] no-underline transition-colors duration-fast',
                    isActive
                      ? 'text-muted-foreground hover:text-primary'
                      : 'text-foreground hover:text-primary',
                  )}
                >
                  {page.name}
                </a>
                <span className="block h-px w-10 bg-border" aria-hidden="true" />
              </div>
            );
          })}
        </nav>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SitemapContent',
  displayName: 'Sitemap',
  tokens: {
    'sitemapcontent.heading': {
      label: 'Page Heading',
      type: 'text',
      defaultValue: 'Sitemap',
    },
  },
  collectionBindings: {
    'sitemapcontent.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(SitemapContent as any).contentSchema = contentSchema;

export default SitemapContent;
